import { logError } from 'setup/logger/logError';
import { getServiceWorkerContainer } from '../serviceWorkerContainer';
import { describeServiceWorkerPresence } from './model';
import { restoreServiceWorker } from './restoreServiceWorker';
import { ServiceWorkerPresence } from './types';

export { describeServiceWorkerPresence } from './model';
export { restoreServiceWorker, SERVICE_WORKER_SCRIPT_URL } from './restoreServiceWorker';
export { ServiceWorkerPresence } from './types';

/** One restoring at a time, and one per page load: a worker the browser keeps removing must not loop. */
let restoring: Promise<boolean> | undefined;
let isRestoreAttempted = false;
/** A worker lost is one the page had: before its first registration (or on a page that never has one) nothing is missing. */
let wasServiceWorkerSeen = false;

async function readPresence(container: ServiceWorkerContainer): Promise<ServiceWorkerPresence> {
  const registration = await container.getRegistration().catch(() => undefined);
  return describeServiceWorkerPresence(registration);
}

async function restoreMissingServiceWorker(container: ServiceWorkerContainer, onUnrestorable: () => void): Promise<boolean> {
  const isRestored = await restoreServiceWorker(container);
  // Once per page load, with the outcome: the next checks stay quiet instead of logging every minute.
  logError('Service worker disappeared during the session', { isRestored });
  if (!isRestored) {
    onUnrestorable();
  }
  return isRestored;
}

/**
 * Whether the page has a service worker to talk to; checked on every keep-alive
 * (interval, visibility, focus). A worker that disappeared is registered again
 * once; when that does not give a usable worker, `onUnrestorable` asks the user
 * to refresh. Resolves `false` while the worker is missing: the caller pauses
 * what needs it. Never rejects. Without the worker API there is nothing to lose.
 */
export async function ensureServiceWorkerPresent(onUnrestorable: () => void): Promise<boolean> {
  const container = getServiceWorkerContainer();
  if (!container) {
    return true;
  }
  if ((await readPresence(container)) === ServiceWorkerPresence.present) {
    wasServiceWorkerSeen = true;
    return true;
  }
  if (!wasServiceWorkerSeen) {
    return true;
  }
  if (restoring) {
    return restoring;
  }
  if (isRestoreAttempted) {
    return false;
  }
  isRestoreAttempted = true;
  restoring = restoreMissingServiceWorker(container, onUnrestorable).finally(() => {
    restoring = undefined;
  });
  return restoring;
}

/** test-only */
export function forgetServiceWorkerRestoreForTests(): void {
  restoring = undefined;
  isRestoreAttempted = false;
  wasServiceWorkerSeen = false;
}
